import { useEffect, useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, CircularProgress, Autocomplete,
} from '@mui/material'
import { Add, MoreVert, Edit, SwapHoriz, Inventory, Delete, Download, Sell } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listAssets, getAsset, createAsset, updateAsset, transferAsset, recordAssetInventoryCount, retireAsset, getAssetExportUrl,
  sellAsset, getAssetMovements, ASSET_CATEGORIES, DEPRECIATION_METHODS, COST_COMPONENT_TYPES, type AssetListItem, type AssetDetail,
  type AssetCategory, type AssetDepreciationMethod, type AssetStatus, type SaveAssetPayload, type SellAssetPayload, type AssetMovementType,
} from '../../api/active'
import { COST_CENTERS, type CostCenter } from '../../api/deconturi'
import { listSuppliers } from '../../api/furnizori'
import { listClients, type ClientListItem } from '../../api/clienti'
import { listVatRates } from '../../api/taxe'
import { listUsers } from '../../api/admin'
import { errorMessage } from '../../api/client'

const STATUS_LABEL: Record<AssetStatus, string> = {
  InCursDePunereInFunctiune: 'În curs de PIF', Functional: 'Funcțional', InMentenanta: 'În mentenanță',
  InConservare: 'În conservare', Casat: 'Casat', Vandut: 'Vândut',
}
const STATUS_COLOR: Record<AssetStatus, 'default' | 'info' | 'success' | 'warning' | 'error' | 'secondary'> = {
  InCursDePunereInFunctiune: 'info', Functional: 'success', InMentenanta: 'warning', InConservare: 'default', Casat: 'error', Vandut: 'secondary',
}
const MOVEMENT_LABEL: Record<AssetMovementType, string> = {
  Intrare: 'Intrare', CostSuplimentar: 'Cost suplimentar', PunereInFunctiune: 'Punere în funcțiune',
  Transfer: 'Transfer', Inventariere: 'Inventariere', Casare: 'Casare', Vanzare: 'Vânzare',
}
const EXITED_STATUSES: AssetStatus[] = ['Casat', 'Vandut']
const CATEGORY_LABEL = Object.fromEntries(ASSET_CATEGORIES.map((c) => [c.value, c.label])) as Record<AssetCategory, string>
const COST_COMPONENT_LABEL = Object.fromEntries(COST_COMPONENT_TYPES.map((c) => [c.value, c.label]))
const COST_CENTER_LABEL = Object.fromEntries(COST_CENTERS.map((c) => [c.value, c.label])) as Record<CostCenter, string>

export default function RegistruActivePage() {
  const [formTarget, setFormTarget] = useState<AssetListItem | null | 'new'>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: AssetListItem } | null>(null)
  const [transferTarget, setTransferTarget] = useState<AssetListItem | null>(null)
  const [countTarget, setCountTarget] = useState<AssetListItem | null>(null)
  const [retireTarget, setRetireTarget] = useState<AssetListItem | null>(null)
  const [sellTarget, setSellTarget] = useState<AssetListItem | null>(null)
  const { data, isLoading } = useQuery({ queryKey: ['active', 'registru'], queryFn: listAssets })
  const closeMenu = () => setMenuAnchor(null)

  const exportMutation = useMutation({
    mutationFn: getAssetExportUrl,
    onSuccess: (blob) => window.open(URL.createObjectURL(blob), '_blank'),
  })

  const columns = useMemo<GridColDef<AssetListItem>[]>(() => [
    { field: 'inventoryNumber', headerName: 'Nr. inventar', width: 150, renderCell: (p) => <code>{p.row.inventoryNumber}</code> },
    { field: 'name', headerName: 'Denumire', flex: 1, minWidth: 200 },
    { field: 'category', headerName: 'Categorie', width: 180, renderCell: (p) => CATEGORY_LABEL[p.row.category] },
    { field: 'location', headerName: 'Locație', width: 160, renderCell: (p) => p.row.location ?? '—' },
    { field: 'costCenter', headerName: 'Centru de cost', width: 140, renderCell: (p) => COST_CENTER_LABEL[p.row.costCenter] },
    { field: 'entryValue', headerName: 'Val. intrare', width: 120, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    { field: 'netBookValue', headerName: 'Val. netă contabilă', width: 150, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    {
      field: 'status', headerName: 'Status', width: 130, sortable: false,
      renderCell: (p) => <Chip size="small" label={STATUS_LABEL[p.row.status]} color={STATUS_COLOR[p.row.status]} variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Active fixe</Typography>
        <Typography variant="inherit" color="text.primary">Registru active</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Registru active fixe</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="outlined" startIcon={<Download />} disabled={exportMutation.isPending} onClick={() => exportMutation.mutate()}>
          Export lunar
        </Button>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Activ nou</Button>
      </Box>
      <Box sx={{ height: 600, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>
      <AssetFormDialog target={formTarget} onClose={() => setFormTarget(null)} />
      <TransferDialog target={transferTarget} onClose={() => setTransferTarget(null)} />
      <InventoryCountDialog target={countTarget} onClose={() => setCountTarget(null)} />
      <RetireDialog target={retireTarget} onClose={() => setRetireTarget(null)} />
      <SellDialog target={sellTarget} onClose={() => setSellTarget(null)} />
      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" disabled={menuAnchor.row.status === 'Casat'} onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          <MenuItem key="transfer" disabled={menuAnchor.row.status === 'Casat'} onClick={() => { setTransferTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><SwapHoriz fontSize="small" /></ListItemIcon><ListItemText>Transferă</ListItemText>
          </MenuItem>,
          <MenuItem key="count" onClick={() => { setCountTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Inventory fontSize="small" /></ListItemIcon><ListItemText>Inventariază</ListItemText>
          </MenuItem>,
          <MenuItem key="retire" disabled={EXITED_STATUSES.includes(menuAnchor.row.status)} onClick={() => { setRetireTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Delete fontSize="small" /></ListItemIcon><ListItemText>Casează</ListItemText>
          </MenuItem>,
          <MenuItem key="sell" disabled={EXITED_STATUSES.includes(menuAnchor.row.status) || menuAnchor.row.status === 'InCursDePunereInFunctiune'}
            onClick={() => { setSellTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Sell fontSize="small" /></ListItemIcon><ListItemText>Vinde</ListItemText>
          </MenuItem>,
        ]}
      </Menu>
    </Stack>
  )
}

const EMPTY_ASSET: SaveAssetPayload = {
  name: '', category: 'IT', subcategory: null, acquisitionDate: new Date().toISOString().slice(0, 10), entryValue: 0,
  supplierId: null, invoiceNumber: null, depreciationDurationMonths: 36, depreciationMethod: 'Liniara', residualValue: 0,
  location: null, responsibleUserId: null, costCenter: 'Administrativ', serialNumber: null, warrantyUntil: null, notes: null,
}

function AssetFormDialog({ target, onClose }: { target: AssetListItem | null | 'new'; onClose: () => void }) {
  const open = target !== null
  const editing = target !== null && target !== 'new'
  const qc = useQueryClient()
  const [form, setForm] = useState<SaveAssetPayload>(EMPTY_ASSET)

  const { data: suppliers } = useQuery({ queryKey: ['achizitii', 'furnizori', 'toti'], queryFn: () => listSuppliers(), enabled: open })
  const { data: users } = useQuery({ queryKey: ['admin', 'utilizatori'], queryFn: () => listUsers(), enabled: open })
  const { data: detail } = useQuery({
    queryKey: ['active', 'activ', editing ? (target as AssetListItem).id : null],
    queryFn: () => getAsset((target as AssetListItem).id), enabled: editing,
  })
  const { data: movements } = useQuery({
    queryKey: ['active', 'miscari', editing ? (target as AssetListItem).id : null],
    queryFn: () => getAssetMovements((target as AssetListItem).id), enabled: editing,
  })

  const detailToForm = (d: AssetDetail): SaveAssetPayload => ({
    name: d.name, category: d.category, subcategory: d.subcategory, acquisitionDate: d.acquisitionDate, entryValue: d.entryValue,
    supplierId: d.supplierId, invoiceNumber: d.invoiceNumber, depreciationDurationMonths: d.depreciationDurationMonths,
    depreciationMethod: d.depreciationMethod, residualValue: d.residualValue, location: d.location,
    responsibleUserId: d.responsibleUserId, costCenter: d.costCenter, serialNumber: d.serialNumber,
    warrantyUntil: d.warrantyUntil, notes: d.notes,
  })

  useEffect(() => {
    if (target === 'new') setForm(EMPTY_ASSET)
    else if (editing && detail) setForm(detailToForm(detail))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, editing, detail])

  const mutation = useMutation({
    mutationFn: (payload: SaveAssetPayload) => editing
      ? updateAsset((target as AssetListItem).id, payload).then(() => undefined)
      : createAsset(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['active', 'registru'] })
      void qc.invalidateQueries({ queryKey: ['active', 'activ'] })
      setForm(EMPTY_ASSET)
      onClose()
    },
  })

  const set = <K extends keyof SaveAssetPayload>(key: K, value: SaveAssetPayload[K]) => setForm((f) => ({ ...f, [key]: value }))

  if (editing && !detail) {
    return <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth><DialogContent><Stack sx={{ py: 4, alignItems: 'center' }}><CircularProgress /></Stack></DialogContent></Dialog>
  }
  if (!open) return null
  const f = form

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? `Activ ${detail?.inventoryNumber}` : 'Activ fix nou'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField label="Denumire" value={f.name} onChange={(e) => set('name', e.target.value)} fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField select label="Categorie" value={f.category} onChange={(e) => set('category', e.target.value as AssetCategory)} fullWidth>
              {ASSET_CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </TextField>
            <TextField label="Subcategorie" value={f.subcategory ?? ''} onChange={(e) => set('subcategory', e.target.value || null)} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField type="date" label="Dată achiziție" value={f.acquisitionDate} onChange={(e) => set('acquisitionDate', e.target.value)}
              fullWidth slotProps={{ inputLabel: { shrink: true } }} disabled={editing} />
            <TextField type="number" label="Valoare de intrare" value={f.entryValue} onChange={(e) => set('entryValue', Number(e.target.value))}
              fullWidth disabled={editing} />
          </Stack>
          <Autocomplete
            options={suppliers?.map((s) => s.id) ?? []} getOptionLabel={(id) => suppliers?.find((s) => s.id === id)?.name ?? id}
            value={f.supplierId} onChange={(_, v) => set('supplierId', v)}
            renderInput={(params) => <TextField {...params} label="Furnizor (opțional)" />}
          />
          <TextField label="Nr. factură" value={f.invoiceNumber ?? ''} onChange={(e) => set('invoiceNumber', e.target.value || null)} fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField type="number" label="Durată amortizare (luni)" value={f.depreciationDurationMonths}
              onChange={(e) => set('depreciationDurationMonths', Number(e.target.value))} fullWidth />
            <TextField select label="Metodă amortizare" value={f.depreciationMethod}
              onChange={(e) => set('depreciationMethod', e.target.value as AssetDepreciationMethod)} fullWidth>
              {DEPRECIATION_METHODS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
            </TextField>
            <TextField type="number" label="Valoare reziduală" value={f.residualValue} onChange={(e) => set('residualValue', Number(e.target.value))} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Locație inițială" value={f.location ?? ''} onChange={(e) => set('location', e.target.value || null)} fullWidth disabled={editing} />
            <TextField select label="Centru de cost" value={f.costCenter} onChange={(e) => set('costCenter', e.target.value as CostCenter)} fullWidth>
              {COST_CENTERS.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
            </TextField>
          </Stack>
          <Autocomplete
            options={users?.map((u) => u.id) ?? []} getOptionLabel={(id) => { const u = users?.find((x) => x.id === id); return u ? `${u.firstName} ${u.lastName}` : id }}
            value={f.responsibleUserId} onChange={(_, v) => set('responsibleUserId', v)}
            renderInput={(params) => <TextField {...params} label="Responsabil (opțional)" />}
          />
          <Stack direction="row" spacing={2}>
            <TextField label="Nr. serie" value={f.serialNumber ?? ''} onChange={(e) => set('serialNumber', e.target.value || null)} fullWidth />
            <TextField type="date" label="Garanție până la" value={f.warrantyUntil ?? ''} onChange={(e) => set('warrantyUntil', e.target.value || null)}
              fullWidth slotProps={{ inputLabel: { shrink: true } }} />
          </Stack>
          <TextField label="Note" value={f.notes ?? ''} onChange={(e) => set('notes', e.target.value || null)} fullWidth multiline minRows={2} />
          {editing && detail && (
            <Alert severity="info">
              {detail.commissionedAt
                ? `Pus în funcțiune la ${detail.commissionedAt} · Amortizare cumulată: ${detail.accumulatedDepreciation.toFixed(2).replace('.', ',')} · Valoare netă contabilă: ${detail.netBookValue.toFixed(2).replace('.', ',')}`
                : 'În curs de punere în funcțiune — nu amortizează încă (vezi ecranul Intrări).'}
            </Alert>
          )}
          {editing && detail && detail.costComponents.length > 0 && (
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">Componente ale valorii de intrare</Typography>
              {detail.costComponents.map((c) => (
                <Stack key={c.id} direction="row" sx={{ fontSize: 13, justifyContent: 'space-between' }}>
                  <span>{COST_COMPONENT_LABEL[c.type]} — {c.description}</span>
                  <span>{c.amount.toFixed(2).replace('.', ',')}</span>
                </Stack>
              ))}
            </Stack>
          )}
          {editing && movements && movements.length > 0 && (
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">Istoric mișcări</Typography>
              {movements.map((m, i) => (
                <Stack key={i} direction="row" sx={{ fontSize: 13, justifyContent: 'space-between' }}>
                  <span>{m.date} — {MOVEMENT_LABEL[m.type]}: {m.description}</span>
                  {m.amount !== null && <span>{m.amount.toFixed(2).replace('.', ',')}</span>}
                </Stack>
              ))}
            </Stack>
          )}
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Renunță</Button>
        <Button variant="contained" disabled={!f.name || mutation.isPending}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={() => mutation.mutate(f)}>
          Salvează
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function TransferDialog({ target, onClose }: { target: AssetListItem | null; onClose: () => void }) {
  const open = target !== null
  const qc = useQueryClient()
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [toLocation, setToLocation] = useState('')
  const [reason, setReason] = useState('')

  const mutation = useMutation({
    mutationFn: () => transferAsset(target!.id, date, toLocation, reason || null),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['active', 'registru'] })
      setToLocation(''); setReason('')
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Transferă — {target?.inventoryNumber}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">Locație curentă: {target?.location ?? '—'}</Typography>
          <TextField type="date" label="Dată" value={date} onChange={(e) => setDate(e.target.value)} fullWidth slotProps={{ inputLabel: { shrink: true } }} />
          <TextField label="Locație nouă" value={toLocation} onChange={(e) => setToLocation(e.target.value)} fullWidth />
          <TextField label="Motiv" value={reason} onChange={(e) => setReason(e.target.value)} fullWidth multiline minRows={2} />
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Renunță</Button>
        <Button variant="contained" disabled={!toLocation || mutation.isPending} onClick={() => mutation.mutate()}>Transferă</Button>
      </DialogActions>
    </Dialog>
  )
}

function InventoryCountDialog({ target, onClose }: { target: AssetListItem | null; onClose: () => void }) {
  const open = target !== null
  const qc = useQueryClient()
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [found, setFound] = useState(true)
  const [notes, setNotes] = useState('')

  const mutation = useMutation({
    mutationFn: () => recordAssetInventoryCount(target!.id, date, found, notes || null),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['active', 'registru'] })
      setNotes(''); setFound(true)
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Inventariază — {target?.inventoryNumber}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField type="date" label="Dată" value={date} onChange={(e) => setDate(e.target.value)} fullWidth slotProps={{ inputLabel: { shrink: true } }} />
          <TextField select label="Rezultat" value={found ? 'gasit' : 'lipsa'} onChange={(e) => setFound(e.target.value === 'gasit')} fullWidth>
            <MenuItem value="gasit">Găsit la locație</MenuItem>
            <MenuItem value="lipsa">Lipsă / nelocalizat</MenuItem>
          </TextField>
          <TextField label="Note" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline minRows={2} />
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Renunță</Button>
        <Button variant="contained" disabled={mutation.isPending} onClick={() => mutation.mutate()}>Salvează</Button>
      </DialogActions>
    </Dialog>
  )
}

function RetireDialog({ target, onClose }: { target: AssetListItem | null; onClose: () => void }) {
  const open = target !== null
  const qc = useQueryClient()
  const [reason, setReason] = useState('')
  const [pendingMessage, setPendingMessage] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => retireAsset(target!.id, reason),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['active', 'registru'] })
      if (r.pendingApproval) setPendingMessage('Casarea a fost trimisă la aprobare.')
      else { setReason(''); onClose() }
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Casează — {target?.inventoryNumber}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField label="Motiv casare" value={reason} onChange={(e) => setReason(e.target.value)} fullWidth multiline minRows={2} required />
          {pendingMessage && <Alert severity="info">{pendingMessage}</Alert>}
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Închide</Button>
        <Button variant="contained" color="error" disabled={!reason || mutation.isPending} onClick={() => mutation.mutate()}>Casează</Button>
      </DialogActions>
    </Dialog>
  )
}

function SellDialog({ target, onClose }: { target: AssetListItem | null; onClose: () => void }) {
  const open = target !== null
  const qc = useQueryClient()
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [buyerClient, setBuyerClient] = useState<ClientListItem | null>(null)
  const [newBuyerName, setNewBuyerName] = useState('')
  const [salePrice, setSalePrice] = useState(0)
  const [currency, setCurrency] = useState('RON')
  const [vatRateId, setVatRateId] = useState<string | null>(null)
  const [pendingMessage, setPendingMessage] = useState<string | null>(null)

  const { data: clients } = useQuery({ queryKey: ['comercial', 'clienti', 'toti'], queryFn: () => listClients(), enabled: open })
  const { data: vatRates } = useQuery({ queryKey: ['financiar', 'cote-tva'], queryFn: listVatRates, enabled: open })

  const reset = () => { setBuyerClient(null); setNewBuyerName(''); setSalePrice(0); setCurrency('RON'); setVatRateId(null); setPendingMessage(null) }

  const mutation = useMutation({
    mutationFn: () => {
      const payload: SellAssetPayload = {
        assetId: target!.id, date, buyerClientId: buyerClient?.id ?? null,
        newBuyerName: buyerClient ? null : newBuyerName.trim(), salePrice, currency, vatRateId,
      }
      return sellAsset(payload)
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['active', 'registru'] })
      void qc.invalidateQueries({ queryKey: ['active', 'iesiri'] })
      if (r.pendingApproval) setPendingMessage('Vânzarea a fost trimisă la aprobare.')
      else { reset(); onClose() }
    },
  })

  const canSubmit = salePrice > 0 && (!!buyerClient || newBuyerName.trim().length > 0)

  return (
    <Dialog open={open} onClose={() => { reset(); onClose() }} maxWidth="sm" fullWidth>
      <DialogTitle>Vinde — {target?.inventoryNumber}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">Valoare netă contabilă curentă: {target?.netBookValue.toFixed(2).replace('.', ',')}</Typography>
          <TextField type="date" label="Dată vânzare" value={date} onChange={(e) => setDate(e.target.value)} fullWidth slotProps={{ inputLabel: { shrink: true } }} />
          <Autocomplete
            options={clients ?? []} getOptionLabel={(c) => c.name} value={buyerClient}
            onChange={(_, v) => setBuyerClient(v)}
            renderInput={(params) => <TextField {...params} label="Cumpărător existent (opțional)" />}
          />
          <TextField label="Sau cumpărător nou" value={newBuyerName} onChange={(e) => setNewBuyerName(e.target.value)}
            fullWidth disabled={!!buyerClient} helperText="Se creează un client minim pentru acest cumpărător." />
          <Stack direction="row" spacing={2}>
            <TextField type="number" label="Preț de vânzare" value={salePrice} onChange={(e) => setSalePrice(Number(e.target.value))} fullWidth />
            <TextField label="Monedă" value={currency} onChange={(e) => setCurrency(e.target.value)} fullWidth />
            <TextField select label="Cotă TVA" value={vatRateId ?? ''} onChange={(e) => setVatRateId(e.target.value || null)} fullWidth>
              <MenuItem value="">Fără TVA</MenuItem>
              {vatRates?.map((v) => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
            </TextField>
          </Stack>
          {pendingMessage && <Alert severity="info">{pendingMessage}</Alert>}
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={() => { reset(); onClose() }}>Închide</Button>
        <Button variant="contained" disabled={!canSubmit || mutation.isPending} onClick={() => mutation.mutate()}>Vinde</Button>
      </DialogActions>
    </Dialog>
  )
}
